import { useCustomForm } from "common/hooks/src/api/useCustomForm";
import { Button } from "components/button";
import { CardWithTitle } from "components/card/CardWithTitle";
import { InputField, ResizableInputField } from "components/fields/InputField";
import { SelectField } from "components/fields/SelectField";
import { useCreateCerfat } from "modules/cerfat/api";
import { CreateCerfatPayload, FluidType, InterventionType, ControlInterval } from "modules/cerfat/types/cerfat.interface";
import { useGetClients } from "modules/client/api";
import { useGetWorkspaceStaff } from "modules/workspaces/api";
import { FormEvent, useMemo } from "react";

const INTERVENTION_OPTIONS: { value: InterventionType; label: string }[] = [
  { value: "assemblage", label: "Assemblage" },
  { value: "miseService", label: "Mise en service" },
  { value: "modification", label: "Modification" },
  { value: "maintenance", label: "Maintenance" },
  { value: "ctrlPerio", label: "Contrôle périodique" },
  { value: "ctrlNonPerio", label: "Contrôle non périodique" },
  { value: "demantelement", label: "Démantèlement" },
  { value: "autre", label: "Autre" },
];

const CONTROL_INTERVAL_OPTIONS: { value: ControlInterval; label: string }[] = [
  { value: "avec-6m", label: "Avec détecteur — 6 mois" },
  { value: "avec-12m", label: "Avec détecteur — 12 mois" },
  { value: "avec-24m", label: "Avec détecteur — 24 mois" },
  { value: "sans-3m", label: "Sans détecteur — 3 mois" },
  { value: "sans-6m", label: "Sans détecteur — 6 mois" },
  { value: "sans-12m", label: "Sans détecteur — 12 mois" },
];

const FLUID_TYPE_OPTIONS: { value: FluidType; label: string }[] = [
  { value: "HCFC", label: "HCFC" },
  { value: "HFC", label: "HFC" },
  { value: "HFO", label: "HFO" },
  { value: "autre-non-inflammable", label: "Autre — non inflammable" },
  { value: "autre-inflammable", label: "Autre — inflammable" },
];

const defaultForm: CreateCerfatPayload = {
  ficheNo: "",
  attestationNo: "",
  clientId: "",
  operatorId: "",
  equipmentId: "",
  fluidName: "",
  totalCharge: 0,
  teqCO2: 0,
  fluidType: undefined,
  interventionTypes: [],
  interventionAutre: "",
  controlInterval: undefined,
  detectorId: "",
  detectorDate: "",
  hasDetectionSystem: false,
  leakingAcknowledge: false,
  leakingLocation: [],
  quantiteChargee: { total: 0, fluideVierge: 0, fluideRecycle: 0, fluideRegenere: 0 },
  quantiteRecuperee: { total: 0, fluideTraitement: 0, fluideReutilisation: 0 },
  denominationFluide: "",
  numeroBSFF: "",
  contenantId: "",
  installationInfo: "",
  observations: "",
};

const CreateCerfa = () => {
  const { mutate } = useCreateCerfat();
  const { data: clientsData } = useGetClients(undefined, 100);
  const { data: staffData } = useGetWorkspaceStaff();

  const clientOptions = useMemo(() => {
    const clients = clientsData?.pages?.flatMap((p) => p.data) || [];
    return [
      { value: "", label: "Sélectionner un client" },
      ...clients.map((c) => ({ value: c._id, label: c.name })),
    ];
  }, [clientsData]);

  const staffOptions = useMemo(() => {
    return [
      { value: "", label: "Sélectionner un opérateur" },
      ...(staffData || []).map((s) => ({ value: s.id, label: s.name })),
    ];
  }, [staffData]);

  const { formValues, setFormValues } = useCustomForm<CreateCerfatPayload>(defaultForm);

  const updateField = (field: string, value: any) =>
    setFormValues({ ...formValues, [field]: value });

  const toggleIntervention = (type: InterventionType) => {
    const current = formValues.interventionTypes || [];
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    updateField("interventionTypes", next);
  };

  const addLeakLocation = () =>
    updateField("leakingLocation", [
      ...(formValues.leakingLocation || []),
      { value: "", status: "todo" },
    ]);

  const removeLeakLocation = (i: number) =>
    updateField(
      "leakingLocation",
      (formValues.leakingLocation || []).filter((_, idx) => idx !== i)
    );

  const updateLeakLocation = (i: number, field: string, value: string) => {
    const updated = (formValues.leakingLocation || []).map((loc, idx) =>
      idx === i ? { ...loc, [field]: value } : loc
    );
    updateField("leakingLocation", updated);
  };

  const isSubmitDisabled =
    !formValues.clientId || !formValues.operatorId || !formValues.fluidName || !formValues.totalCharge;

  const handleSubmit = (e: FormEvent) => {
    if (isSubmitDisabled) return;
    e.preventDefault();
    mutate(formValues);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Identification */}
      <CardWithTitle title="Identification">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputField variant="auth" extra="" label="N° de fiche" placeholder="ex: 2024-001" id="ficheNo" type="text" value={formValues.ficheNo || ""} onChange={(e) => updateField("ficheNo", e.target.value)} />
          <InputField variant="auth" extra="" label="N° d'attestation" placeholder="ex: ATT-001" id="attestationNo" type="text" value={formValues.attestationNo || ""} onChange={(e) => updateField("attestationNo", e.target.value)} />
        </div>
      </CardWithTitle>

      {/* Parties */}
      <CardWithTitle title="Parties">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField label="Client (détenteur) *" options={clientOptions} value={formValues.clientId} onChange={(e) => updateField("clientId", e.target.value)} />
          <SelectField label="Opérateur *" options={staffOptions} value={formValues.operatorId} onChange={(e) => updateField("operatorId", e.target.value)} />
        </div>
      </CardWithTitle>

      {/* Équipement */}
      <CardWithTitle title="Équipement">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputField variant="auth" extra="" label="Identifiant équipement" placeholder="ex: CLI-001-PAC" id="equipmentId" type="text" value={formValues.equipmentId || ""} onChange={(e) => updateField("equipmentId", e.target.value)} />
          <InputField variant="auth" extra="" label="Fluide frigorigène *" placeholder="ex: R-410A" id="fluidName" type="text" value={formValues.fluidName} onChange={(e) => updateField("fluidName", e.target.value)} />
          <InputField variant="auth" extra="" label="Charge totale (kg) *" placeholder="0" id="totalCharge" type="number" value={String(formValues.totalCharge || "")} onChange={(e) => updateField("totalCharge", parseFloat(e.target.value) || 0)} />
          <InputField variant="auth" extra="" label="Équivalent CO₂ (tCO₂)" placeholder="0" id="teqCO2" type="number" value={String(formValues.teqCO2 || "")} onChange={(e) => updateField("teqCO2", parseFloat(e.target.value) || 0)} />
          <SelectField
            label="Type de fluide"
            options={[{ value: "", label: "Sélectionner" }, ...FLUID_TYPE_OPTIONS]}
            value={formValues.fluidType || ""}
            onChange={(e) => updateField("fluidType", e.target.value || undefined)}
          />
        </div>
      </CardWithTitle>

      {/* Types d'intervention */}
      <CardWithTitle title="Types d'intervention">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {INTERVENTION_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={(formValues.interventionTypes || []).includes(opt.value)}
                onChange={() => toggleIntervention(opt.value)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>
        {(formValues.interventionTypes || []).includes("autre") && (
          <div className="mt-3">
            <InputField variant="auth" extra="" label="Préciser (autre)" placeholder="Décrire l'intervention" id="interventionAutre" type="text" value={formValues.interventionAutre || ""} onChange={(e) => updateField("interventionAutre", e.target.value)} />
          </div>
        )}
      </CardWithTitle>

      {/* Contrôle d'étanchéité */}
      <CardWithTitle title="Intervalle de contrôle d'étanchéité">
        <SelectField
          label="Fréquence de contrôle"
          options={[{ value: "", label: "Sélectionner" }, ...CONTROL_INTERVAL_OPTIONS]}
          value={formValues.controlInterval || ""}
          onChange={(e) => updateField("controlInterval", e.target.value || undefined)}
        />
      </CardWithTitle>

      {/* Détecteur */}
      <CardWithTitle title="Détecteur de fuite">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputField variant="auth" extra="" label="Identifiant détecteur" placeholder="ex: DET-001" id="detectorId" type="text" value={formValues.detectorId || ""} onChange={(e) => updateField("detectorId", e.target.value)} />
          <InputField variant="auth" extra="" label="Date de dernier contrôle" placeholder="" id="detectorDate" type="date" value={formValues.detectorDate ? String(formValues.detectorDate).substring(0, 10) : ""} onChange={(e) => updateField("detectorDate", e.target.value)} />
        </div>
      </CardWithTitle>

      {/* Système de détection permanente */}
      <CardWithTitle title="Système permanent de détection de fuites [6]">
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="hasDetectionSystem" checked={formValues.hasDetectionSystem === true} onChange={() => updateField("hasDetectionSystem", true)} className="h-4 w-4" />
            <span className="text-sm text-gray-700">OUI</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="hasDetectionSystem" checked={formValues.hasDetectionSystem === false} onChange={() => updateField("hasDetectionSystem", false)} className="h-4 w-4" />
            <span className="text-sm text-gray-700">NON</span>
          </label>
        </div>
      </CardWithTitle>

      {/* Fuites */}
      <CardWithTitle title="Fuites constatées [10]">
        <div className="mb-3 flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="leakingAcknowledge" checked={formValues.leakingAcknowledge === true} onChange={() => updateField("leakingAcknowledge", true)} className="h-4 w-4" />
            <span className="text-sm text-gray-700">Fuite détectée</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="leakingAcknowledge" checked={formValues.leakingAcknowledge === false} onChange={() => updateField("leakingAcknowledge", false)} className="h-4 w-4" />
            <span className="text-sm text-gray-700">Aucune fuite</span>
          </label>
        </div>
        <div className="space-y-3">
          {(formValues.leakingLocation || []).map((loc, i) => (
            <div key={i} className="flex items-end gap-3">
              <div className="flex-1">
                <InputField variant="auth" extra="" label={`Localisation ${i + 1}`} placeholder="ex: Compresseur" id={`leak-loc-${i}`} type="text" value={loc.value} onChange={(e) => updateLeakLocation(i, "value", e.target.value)} />
              </div>
              <div className="w-36">
                <SelectField label="Statut" options={[{ value: "todo", label: "À faire" }, { value: "done", label: "Réalisée" }]} value={loc.status} onChange={(e) => updateLeakLocation(i, "status", e.target.value)} />
              </div>
              <button type="button" onClick={() => removeLeakLocation(i)} className="mb-0.5 rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          ))}
          {(formValues.leakingLocation || []).length < 3 && (
            <button type="button" onClick={addLeakLocation} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-medium text-gray-500 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Ajouter une localisation
            </button>
          )}
        </div>
      </CardWithTitle>

      {/* Quantités de fluide */}
      <CardWithTitle title="Quantités de fluide chargé">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <InputField variant="auth" extra="" label="Total (kg)" placeholder="0" id="qc-total" type="number" value={String(formValues.quantiteChargee?.total || "")} onChange={(e) => updateField("quantiteChargee", { ...formValues.quantiteChargee, total: parseFloat(e.target.value) || 0 })} />
          <InputField variant="auth" extra="" label="QA — Vierge" placeholder="0" id="qc-vierge" type="number" value={String(formValues.quantiteChargee?.fluideVierge || "")} onChange={(e) => updateField("quantiteChargee", { ...formValues.quantiteChargee, fluideVierge: parseFloat(e.target.value) || 0 })} />
          <InputField variant="auth" extra="" label="QB — Recyclé" placeholder="0" id="qc-recycle" type="number" value={String(formValues.quantiteChargee?.fluideRecycle || "")} onChange={(e) => updateField("quantiteChargee", { ...formValues.quantiteChargee, fluideRecycle: parseFloat(e.target.value) || 0 })} />
          <InputField variant="auth" extra="" label="QC — Régénéré" placeholder="0" id="qc-regene" type="number" value={String(formValues.quantiteChargee?.fluideRegenere || "")} onChange={(e) => updateField("quantiteChargee", { ...formValues.quantiteChargee, fluideRegenere: parseFloat(e.target.value) || 0 })} />
        </div>
      </CardWithTitle>

      <CardWithTitle title="Quantités de fluide récupéré">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <InputField variant="auth" extra="" label="QDE — Total (kg)" placeholder="0" id="qr-total" type="number" value={String(formValues.quantiteRecuperee?.total || "")} onChange={(e) => updateField("quantiteRecuperee", { ...formValues.quantiteRecuperee, total: parseFloat(e.target.value) || 0 })} />
          <InputField variant="auth" extra="" label="QD — Traitement" placeholder="0" id="qr-trait" type="number" value={String(formValues.quantiteRecuperee?.fluideTraitement || "")} onChange={(e) => updateField("quantiteRecuperee", { ...formValues.quantiteRecuperee, fluideTraitement: parseFloat(e.target.value) || 0 })} />
          <InputField variant="auth" extra="" label="QE — Réutilisation" placeholder="0" id="qr-reut" type="number" value={String(formValues.quantiteRecuperee?.fluideReutilisation || "")} onChange={(e) => updateField("quantiteRecuperee", { ...formValues.quantiteRecuperee, fluideReutilisation: parseFloat(e.target.value) || 0 })} />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <InputField variant="auth" extra="" label="Dénomination du fluide" placeholder="ex: R-410A HFC" id="denominationFluide" type="text" value={formValues.denominationFluide || ""} onChange={(e) => updateField("denominationFluide", e.target.value)} />
          <InputField variant="auth" extra="" label="N° BSFF" placeholder="" id="numeroBSFF" type="text" value={formValues.numeroBSFF || ""} onChange={(e) => updateField("numeroBSFF", e.target.value)} />
          <InputField variant="auth" extra="" label="ID contenant" placeholder="" id="contenantId" type="text" value={formValues.contenantId || ""} onChange={(e) => updateField("contenantId", e.target.value)} />
        </div>
      </CardWithTitle>

      {/* Autres informations */}
      <CardWithTitle title="Informations complémentaires">
        <div className="space-y-4">
          <ResizableInputField variant="auth" extra="" label="Installation (section 13)" placeholder="Informations sur l'installation..." id="installationInfo" value={formValues.installationInfo || ""} onChange={(e) => updateField("installationInfo", e.target.value)} />
          <ResizableInputField variant="auth" extra="" label="Observations (section 14)" placeholder="Observations..." id="observations" value={formValues.observations || ""} onChange={(e) => updateField("observations", e.target.value)} />
        </div>
      </CardWithTitle>

      <div className="flex justify-end pb-4">
        <div className="w-full max-w-[200px]">
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            Créer le CERFA
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateCerfa;
